import {
  archTeethSchema,
  getArchFromTooth,
  ItemTypeEnum,
  ProductTypeEnum,
  splintSchema,
  type SplintProduct,
} from "@jasper/client-models";

import {
  TeethSelectionEnum,
  type ProductWorkflowConfiguration,
} from "./ProductWorkflowConfiguration";
import { StepKeysEnum } from "../../steps/types";
import { ArchStep, SplintTypeStep } from "./constants";
import { isSplintItemType } from "@jasper/forms";

export type SplintStepKey =
  typeof StepKeysEnum.arch | typeof StepKeysEnum.splintType;

export const SplintWorkflowConfiguration: ProductWorkflowConfiguration<
  SplintProduct,
  SplintStepKey
> = {
  productType: ProductTypeEnum.splint,
  itemType: ItemTypeEnum.splint,
  isBulkCompatible: (leaderProduct, followerProduct) => {
    if (followerProduct.type !== ProductTypeEnum.splint) {
      return false;
    }

    const leaderProductSplintType = leaderProduct.items?.[0]?.type;
    const followerProductSplintType = followerProduct.items?.[0]?.type;

    return leaderProductSplintType === followerProductSplintType;
  },
  steps: [
    new ArchStep({
      applyArchesSelection: ({ teeth, product, itemType }) => {
        const arch = getArchFromTooth(teeth[0]);
        if (!arch) {
          throw new Error("Invalid teeth selection for arch");
        }

        const splintItem = product.items.find(item =>
          isSplintItemType(item.type)
        );

        return {
          productId: product.id,
          teeth,
          items: [
            {
              type: splintItem?.type ?? itemType,
              materialCharacteristics: splintItem?.materialCharacteristics,
              arch,
            },
          ],
        };
      },
    }),
    new SplintTypeStep(),
  ],
  teethSelection: TeethSelectionEnum.ARCH,
  isBulkSupported: true,

  isValidProduct(product) {
    return splintSchema.safeParse(product).success;
  },

  getCompletedSteps(product) {
    const completedSteps: SplintStepKey[] = [];
    if (archTeethSchema.safeParse(product.teeth).success) {
      completedSteps.push(StepKeysEnum.arch);
    }

    if (this.isValidProduct(product)) {
      completedSteps.push(StepKeysEnum.splintType);
    }

    return completedSteps;
  },
};
